import React, { useEffect, useState } from 'react';
import { Link2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { transportApi } from '@/services/transportApi';

const route = (r) => r.route_summary || `${r.pickup || '—'} → ${r.drop || '—'}`;
const key = (s) => (s.location_id ? `${s.kind}|${s.location_id}` : `${s.kind}|${(s.label || '').trim().toLowerCase()}|${s.lat ?? ''}|${s.lng ?? ''}`);

const km = (a, b) => { const r = (x) => (x * Math.PI) / 180; const dLat = r(b.lat - a.lat), dLng = r(b.lng - a.lng); return Math.asin(Math.sqrt(Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2)); };
const pinned = (s) => s && s.lat != null && s.lng != null;
const nearestOrder = (start, list) => {
  const left = list.filter(pinned); const out = []; let cur = start;
  while (left.length) { const i = pinned(cur) ? left.reduce((bi, s, j) => (km(cur, s) < km(cur, left[bi]) ? j : bi), 0) : 0; cur = left.splice(i, 1)[0]; out.push(cur); }
  return [...out, ...list.filter((s) => !pinned(s))];
};

/** Preview of the merged route (same rules as the server): same place merged once, pickups first, nearest next. */
const mergedPreview = (main, list) => {
  const seen = new Set(); const all = [];
  [main, ...list.filter((r) => r.id !== main.id)].forEach((r) => (r.stops || []).forEach((s) => { const k = key(s); if (!seen.has(k)) { seen.add(k); all.push(s); } }));
  const ups = all.filter((s) => s.kind === 'pickup');
  const orderedUps = [...ups.slice(0, 1), ...nearestOrder(ups[0], ups.slice(1))];
  const drops = nearestOrder(orderedUps[orderedUps.length - 1], all.filter((s) => s.kind !== 'pickup'));
  const names = (arr, kind) => arr.map((s) => s.label || kind).join(', ') || '—';
  return { count: all.length, text: `${names(orderedUps, 'pickup')} → ${names(drops, 'drop')}` };
};

/** Transport Manager / Admin clubs 2+ PENDING requests into one trip. */
export const ClubRequestsDialog = ({ requests, open, onClose, onDone }) => {
  const [mainId, setMainId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const sorted = [...(requests || [])].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));

  useEffect(() => { if (open) { setMainId(sorted[0]?.id || ''); setNote(''); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const main = sorted.find((r) => r.id === mainId) || sorted[0];
  const types = [...new Set(sorted.map((r) => r.vehicle_type).filter(Boolean))];
  const preview = main ? mergedPreview(main, sorted) : null;

  const submit = async () => {
    setBusy(true);
    try {
      await transportApi.clubRequests({ request_ids: sorted.map((r) => r.id), main_id: main.id, note });
      toast.success(`Clubbed ${sorted.length} requests into one trip`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to club requests'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="club-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4 text-violet-600" />Club {sorted.length} requests into one trip</DialogTitle>
          <DialogDescription>Pick the main request. The other requests' stops are added to it and they show as Clubbed until the trip is delivered.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="club-list">
          {sorted.map((r) => (
            <button type="button" key={r.id} onClick={() => setMainId(r.id)} data-testid={`club-main-${r.id}`}
              className={`w-full text-left rounded-lg border p-2.5 transition-colors ${main?.id === r.id ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold">{route(r)}</p>
                {main?.id === r.id && <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700" data-testid={`club-main-badge-${r.id}`}>Main</span>}
              </div>
              <p className="text-xs text-gray-500">{r.section} · {r.requested_by_name || '—'} · {r.vehicle_type}</p>
            </button>
          ))}
        </div>
        {types.length > 1 && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5 flex items-start gap-1" data-testid="club-type-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Vehicle types differ ({types.join(', ')}). The trip uses the main request's type ({main?.vehicle_type}) — you can change it while dispatching.
          </p>
        )}
        {preview && (
          <div className="rounded-lg bg-gray-50 p-2.5" data-testid="club-preview">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Merged route · {preview.count} stops</p>
            <p className="text-sm mt-0.5" data-testid="club-preview-route">{preview.text}</p>
          </div>
        )}
        <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. same direction, one vehicle" data-testid="club-note-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="club-cancel-btn">Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={submit} disabled={busy || sorted.length < 2} data-testid="club-confirm-btn">{busy ? 'Clubbing…' : 'Club into one trip'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
